/**
 * Ажилтан — БҮРТГЭЛ: хэрэглэгч үүсгэх, эрх, нууц үг, TOTP.
 *
 * ⚠️ «Ажилтан» хуудас (/staff) нь одоо зөвхөн ҮЙЛ АЖИЛЛАГАА харуулна:
 *    хэн юу хийсэн, хэдэн шошго, сүүлийн үйлдэл. Хэрэглэгч үүсгэх,
 *    засах, нууц үг сэргээх нь ЭНД.
 *
 * ⚠️ Бүх бичих үйлдэл adminOnly — сервер мөн адил шалгана (ref.ts).
 *    UI-д нуух нь хамгаалалт БИШ.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDateTime, formatPhone, formatQty, normalizePhone } from '@boots/domain';
import { api, type Envelope } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { ROLE_LABEL, type Store, type TotpEnroll, type UserRole } from '../../lib/types';
import {
  Button, Empty, ErrorNote, Field, Input, Modal, OkNote, Select, Spinner,
} from '../../components/ui';
import { DataTable, type Column } from '../../components/data-table';

/**
 * UserForm-д хангалттай хамгийн бага хэлбэр. `/ref/users` ба
 * `/reports/staff` хоёулаа үүнийг хангана — хоёр төрөл зохиохгүй.
 */
interface EditableUser {
  id: string;
  username: string;
  fullName: string;
  role: UserRole;
  phone?: string | null;
  storeName?: string | null;
  isActive: boolean;
}

interface RefUser extends EditableUser {
  /** Богино хугацаатай presigned GET (`withAvatar`). Зураггүй бол null. */
  avatarUrl: string | null;
  totpEnabled: boolean;
  lastLoginAt: string | null;
  store: { id: string; code: string; name: string } | null;
}

/** Зургийн дээд хэмжээ. Гар утас 44px-д харуулна — том файл утгагүй. */
const AVATAR_MAX_BYTES = 2 * 1024 * 1024;
const AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Ажилтны зураг — сонгох, солих, устгах.
 *
 * Урсгал нь гутлын зурагтай ИЖИЛ: presign → шууд S3 руу PUT → confirm.
 * Зураг серверээр дамжихгүй (CLAUDE.md §Аюулгүй байдал).
 */
function AvatarField({ user, onDone }: { user: EditableUser & { avatarUrl?: string | null }; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [url, setUrl] = useState(user.avatarUrl ?? null);

  const upload = async (file: File) => {
    setErr(null);
    if (!AVATAR_TYPES.includes(file.type)) return setErr('JPEG, PNG эсвэл WebP байх ёстой');
    if (file.size > AVATAR_MAX_BYTES) return setErr('Зураг 2MB-аас бага байх ёстой');
    setBusy(true);
    try {
      const pre = await api<Envelope<{ uploadUrl: string }>>(
        `/ref/users/${user.id}/avatar/presign`,
        { method: 'POST', body: { contentType: file.type } },
      );
      // ⚠️ Content-Type нь presign-тэй ЯГ таарах ёстой — гарын үсэгт орсон
      const put = await fetch(pre.data.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!put.ok) throw new Error(`Байршуулалт амжилтгүй (${put.status})`);
      const res = await api<Envelope<{ avatarUrl: string | null }>>(
        `/ref/users/${user.id}/avatar/confirm`,
        { method: 'POST', body: { contentType: file.type } },
      );
      setUrl(res.data.avatarUrl);
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Зураг байршуулж чадсангүй');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api(`/ref/users/${user.id}/avatar`, { method: 'DELETE' });
      setUrl(null);
      onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Field label="Зураг">
      <div className="flex items-center gap-3">
        {url ? (
          <img src={url} alt={user.fullName} className="size-14 rounded-full object-cover" />
        ) : (
          <span className="flex size-14 items-center justify-center rounded-full bg-well text-ink-2">
            {user.fullName.split(' ').map((w) => w[0]).slice(0, 2).join('')}
          </span>
        )}
        <label className="cursor-pointer rounded-full border border-hair px-3.5 py-2 text-[13px] text-accent-700">
          {busy ? 'Байршуулж байна…' : url ? 'Солих' : 'Зураг сонгох'}
          <input
            type="file"
            accept={AVATAR_TYPES.join(',')}
            className="hidden"
            disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void upload(f);
            }}
          />
        </label>
        {url ? (
          <Button variant="ghost" onClick={() => void remove()} disabled={busy}>
            Устгах
          </Button>
        ) : null}
      </div>
      {err ? <ErrorNote>{err}</ErrorNote> : null}
    </Field>
  );
}

const COLUMNS: Column<RefUser>[] = [
  {
    key: 'name',
    header: 'Ажилтан',
    mobile: 'title',
    cell: (u) => (
      <span className="flex items-center gap-2.5">
        {u.avatarUrl ? (
          <img src={u.avatarUrl} alt="" className="size-[26px] shrink-0 rounded-full object-cover" />
        ) : (
          <span className="flex size-[26px] shrink-0 items-center justify-center rounded-full bg-well text-[11px] text-ink-2">
            {u.fullName.split(' ').map((w) => w[0]).slice(0, 2).join('')}
          </span>
        )}
        <span className="flex min-w-0 flex-col">
          <span className="text-pretty">{u.fullName}</span>
          <span className="num text-[11.5px] text-ink-3">@{u.username}</span>
        </span>
      </span>
    ),
  },
  { key: 'role', header: 'Эрх', mobile: 'value', cell: (u) => ROLE_LABEL[u.role] },
  { key: 'store', header: 'Салбар', cell: (u) => u.store?.name ?? '—' },
  { key: 'phone', header: 'Утас', nowrap: true, cell: (u) => (u.phone ? formatPhone(u.phone) : '—') },
  {
    key: 'login',
    header: 'Сүүлд нэвтэрсэн',
    align: 'right',
    cell: (u) => (u.lastLoginAt ? formatDateTime(u.lastLoginAt) : '—'),
  },
  {
    key: 'state',
    header: 'Төлөв',
    cell: (u) =>
      u.isActive ? (
        <span className="rounded-full bg-accent-100 px-2.5 py-[3px] text-[11.5px] text-accent-800">
          Идэвхтэй
        </span>
      ) : (
        <span className="text-ink-3">Идэвхгүй</span>
      ),
  },
];

export function SetupStaffPage() {
  const { can, readOnly } = useAuth();
  const [editing, setEditing] = useState<RefUser | 'new' | null>(null);
  const canWrite = can('ADMIN') && !readOnly;

  const q = useQuery({
    queryKey: ['ref-users'],
    queryFn: () => api<Envelope<RefUser[]>>('/ref/users'),
  });

  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorNote>{(q.error as Error).message}</ErrorNote>;

  const rows = q.data.data;

  return (
    <div className="flex flex-col gap-3">
      <section className="flex flex-col gap-3 rounded-card border border-hair bg-panel px-card py-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-base font-semibold">Хэрэглэгчид</h2>
          <span className="num ml-auto text-[12.5px] text-ink-2">
            {formatQty(rows.filter((u) => u.isActive).length)} идэвхтэй / {formatQty(rows.length)}
          </span>
          {canWrite ? (
            <Button onClick={() => setEditing('new')}>＋ Ажилтан нэмэх</Button>
          ) : (
            <span className="text-[11.5px] text-ink-3">Ажилтан нэмэх эрх зөвхөн админд</span>
          )}
        </div>

        <DataTable
          rows={rows}
          columns={COLUMNS}
          rowKey={(u) => u.id}
          onRowClick={canWrite ? (u) => setEditing(u) : undefined}
          rowClass={(u) => (u.isActive ? '' : 'opacity-55')}
          empty={<Empty>Ажилтан бүртгэгдээгүй</Empty>}
        />
      </section>

      {can('ADMIN') ? <TotpSection /> : null}

      {editing ? (
        <UserForm
          user={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onDone={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------ Хэрэглэгч
const ROLES: UserRole[] = [
  'ADMIN', 'DIRECTOR_READONLY', 'WAREHOUSE', 'STORE_MANAGER', 'SALESPERSON', 'COLLECTOR', 'ACCOUNTANT',
];

function UserForm({ user, onClose, onDone }: {
  user: EditableUser | null; onClose: () => void; onDone: () => void;
}) {
  const qc = useQueryClient();
  const isNew = !user;
  const [username, setUsername] = useState(user?.username ?? '');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState(user?.fullName ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [role, setRole] = useState<UserRole>(user?.role ?? 'SALESPERSON');
  const [storeId, setStore] = useState('');
  const [isActive, setActive] = useState(user?.isActive !== false);
  const [resetting, setResetting] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [note, setNote] = useState<string | null>(null);

  const stores = useQuery({ queryKey: ['ref-stores'], queryFn: () => api<Envelope<Store[]>>('/ref/stores') });

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['staff-report'] });
    await qc.invalidateQueries({ queryKey: ['ref-users'] });
  };

  const save = useMutation({
    mutationFn: () =>
      isNew
        ? api('/ref/users', {
            method: 'POST',
            body: {
              username: username.toLowerCase(), password, fullName, role,
              ...(phone.trim() ? { phone: normalizePhone(phone) ?? phone.trim() } : {}),
              ...(storeId ? { storeId } : {}),
            },
          })
        : api(`/ref/users/${user.id}`, {
            method: 'PATCH',
            body: {
              fullName, role, isActive,
              phone: phone.trim() ? normalizePhone(phone) ?? phone.trim() : null,
              ...(storeId ? { storeId } : {}),
            },
          }),
    onSuccess: async () => { await refresh(); onDone(); },
  });

  const reset = useMutation({
    mutationFn: () =>
      api(`/ref/users/${user!.id}/reset-password`, { method: 'POST', body: { newPassword } }),
    onSuccess: () => { setResetting(false); setNewPassword(''); setNote('Нууц үг шинэчлэгдлээ'); },
  });

  const ready = isNew ? username.length >= 3 && password.length >= 8 && fullName.length >= 2 : fullName.length >= 2;

  return (
    <Modal
      title={isNew ? 'Шинэ ажилтан' : user.fullName}
      subtitle={isNew ? undefined : `@${user.username}`}
      onClose={onClose}
      footer={
        <>
          {!isNew ? (
            <Button variant="ghost" onClick={() => setResetting((v) => !v)} className="mr-auto">
              Нууц үг сэргээх
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>Болих</Button>
          <Button onClick={() => save.mutate()} disabled={!ready || save.isPending}>
            {save.isPending ? 'Хадгалж байна…' : 'Хадгалах'}
          </Button>
        </>
      }
    >
      {note ? <OkNote>{note}</OkNote> : null}

      {isNew ? (
        <>
          <Field label="Нэвтрэх нэр (латин жижиг)">
            <Input value={username} onChange={(e) => setUsername(e.target.value.toLowerCase())} className="num" />
          </Field>
          <Field label="Нууц үг (8-аас дээш тэмдэгт)">
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
        </>
      ) : null}
      {!isNew ? <AvatarField user={user} onDone={() => void refresh()} /> : null}
      <Field label="Бүтэн нэр"><Input value={fullName} onChange={(e) => setFullName(e.target.value)} /></Field>
      <Field label="Утас"><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="99112233" className="num" /></Field>
      <Field label="Үүрэг">
        <Select value={role} onChange={(v) => setRole(v as UserRole)}
          options={ROLES.map((r) => ({ value: r, label: ROLE_LABEL[r] }))} />
      </Field>
      <Field label="Салбар">
        <Select value={storeId} onChange={setStore} placeholder={user?.storeName ?? 'Хамаарахгүй'}
          options={(stores.data?.data ?? []).map((s) => ({ value: s.id, label: s.name }))} />
      </Field>
      {!isNew ? (
        <label className="flex items-center gap-2 text-[13px] text-ink-2">
          <input type="checkbox" checked={isActive} onChange={(e) => setActive(e.target.checked)} className="size-4 accent-accent" />
          Идэвхтэй бүртгэл
        </label>
      ) : null}

      {resetting ? (
        <div className="flex flex-col gap-2 rounded-card border border-hair bg-well p-3">
          <Field label="Шинэ нууц үг">
            <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          </Field>
          <Button
            variant="danger"
            onClick={() => reset.mutate()}
            disabled={newPassword.length < 8 || reset.isPending}
          >
            {reset.isPending ? 'Сэргээж байна…' : 'Нууц үг солих'}
          </Button>
          {reset.error ? <ErrorNote>{(reset.error as Error).message}</ErrorNote> : null}
        </div>
      ) : null}

      {role === 'DIRECTOR_READONLY' ? (
        <p className="text-[11.5px] text-ink-3">
          Захирлын эрхэд хоёр алхамт нэвтрэлт заавал — доорх «Хоёр алхамт нэвтрэлт» хэсгээс тохируулна.
        </p>
      ) : null}
      {save.error ? <ErrorNote>{(save.error as Error).message}</ErrorNote> : null}
    </Modal>
  );
}

/**
 * TOTP удирдлага — зөвхөн ADMIN.
 *
 * Захирал өөрөө тохируулж чадахгүй: `readOnlyGuard` нь DIRECTOR_READONLY-ийн
 * бүх non-GET хүсэлтийг хаадаг. Тиймээс админ нууцыг үүсгээд захиралд
 * дамжуулж, код баталгаажсаны дараа л DB-д бичигдэнэ.
 *
 * QR зурдаггүй — `otpauth://` URI-г текстээр өгнө. Ингэснээр QR сан
 * гэсэн нэмэлт dependency хэрэггүй (CLAUDE.md).
 */
function TotpSection() {
  const qc = useQueryClient();
  const [enroll, setEnroll] = useState<{ userId: string; data: TotpEnroll } | null>(null);
  const [code, setCode] = useState('');
  const [note, setNote] = useState<string | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: ['ref-users'],
    queryFn: () => api<Envelope<RefUser[]>>('/ref/users'),
  });

  const start = useMutation({
    mutationFn: (userId: string) =>
      api<Envelope<TotpEnroll>>(`/ref/users/${userId}/totp`, { method: 'POST' }),
    onSuccess: (res, userId) => {
      setEnroll({ userId, data: res.data });
      setCode('');
      setNote(null);
    },
    onError: (e: Error) => setNote(e.message),
  });

  const confirm = useMutation({
    mutationFn: () =>
      api<void>(`/ref/users/${enroll!.userId}/totp/confirm`, {
        method: 'POST',
        body: { enrollToken: enroll!.data.enrollToken, code },
      }),
    onSuccess: async () => {
      setEnroll(null);
      setCode('');
      setNote('TOTP тохирогдлоо.');
      await qc.invalidateQueries({ queryKey: ['ref-users'] });
    },
    onError: (e: Error) => setNote(e.message),
  });

  const disable = useMutation({
    mutationFn: (userId: string) => api<void>(`/ref/users/${userId}/totp`, { method: 'DELETE' }),
    onSuccess: async () => {
      setNote('TOTP цуцлагдлаа.');
      await qc.invalidateQueries({ queryKey: ['ref-users'] });
    },
    onError: (e: Error) => setNote(e.message),
  });

  if (isPending) return <Spinner />;
  if (error) return <ErrorNote>{(error as Error).message}</ErrorNote>;

  const users = data.data.filter((u) => u.role === 'DIRECTOR_READONLY' || u.totpEnabled);

  return (
    <section className="flex flex-col gap-3 rounded-card border border-hair bg-panel px-card py-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-semibold">Хоёр алхамт баталгаажуулалт</h2>
        <span className="text-[12.5px] text-ink-2">Захирлын эрхэд заавал</span>
      </div>

      {users.length === 0 ? (
        <p className="py-4 text-sm text-ink-2">Захирлын эрхтэй хэрэглэгч алга</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {users.map((u) => (
            <li
              key={u.id}
              className="flex flex-wrap items-center gap-3 rounded-card border border-hair px-3 py-2.5"
            >
              <span className="flex flex-col">
                <span>{u.fullName}</span>
                <span className="num text-[11.5px] text-ink-3">
                  {u.username} · {ROLE_LABEL[u.role]}
                </span>
              </span>
              <span
                className={`rounded-full px-2.5 py-[3px] text-[11px] whitespace-nowrap ${
                  u.totpEnabled ? 'bg-accent-100 text-accent-700' : 'bg-alert-bg text-alert-ink'
                }`}
              >
                {u.totpEnabled ? 'Тохирсон' : 'Тохируулаагүй'}
              </span>
              <span className="ml-auto flex gap-2">
                {u.totpEnabled ? (
                  <Button
                    variant="ghost"
                    onClick={() => disable.mutate(u.id)}
                    disabled={disable.isPending}
                  >
                    Цуцлах
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    onClick={() => start.mutate(u.id)}
                    disabled={start.isPending}
                  >
                    Тохируулах
                  </Button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {enroll ? (
        <div className="flex flex-col gap-3 rounded-card border border-accent-300 bg-well p-3.5">
          <p className="text-sm text-ink-2">
            Доорх нууцыг баталгаажуулах аппад гараар оруулах эсвэл холбоосыг ашиглана уу.
            Дараа нь апп дээр гарсан 6 оронтой кодыг оруулж баталгаажуулна.
          </p>
          <code className="num rounded-card border border-hair bg-panel px-3 py-2 text-[13px] tracking-wider break-all select-all">
            {enroll.data.secret}
          </code>
          <code className="rounded-card border border-hair bg-panel px-3 py-2 text-[11.5px] break-all select-all">
            {enroll.data.otpauthUri}
          </code>
          <div className="flex flex-wrap items-end gap-3">
            <span className="w-[160px]">
              <Field label="Баталгаажуулах код">
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  className="num text-center tracking-[0.3em]"
                />
              </Field>
            </span>
            <Button
              onClick={() => confirm.mutate()}
              disabled={code.length !== 6 || confirm.isPending}
            >
              Баталгаажуулах
            </Button>
            <Button variant="ghost" onClick={() => { setEnroll(null); setCode(''); }}>
              Болих
            </Button>
          </div>
        </div>
      ) : null}

      {note ? <p className="text-sm text-ink-2">{note}</p> : null}

      <p className="text-[11.5px] text-ink-3">
        Утас алдагдвал админ «Цуцлах» дарж л сэргээнэ. Тиймээс ADMIN эрхтэй
        хоёр данс байнга байлгах хэрэгтэй.
      </p>
    </section>
  );
}

